import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  deriveSidebarPalette, normalizeHex, SIDEBAR_CUSTOM_VAR_NAMES,
  PANEL_THEMES, DEFAULT_PANEL_THEME, DEFAULT_PANEL_CUSTOM_COLOR, derivePanelVars, PANEL_CUSTOM_VAR_NAMES,
  deriveChartPalette, deriveChartVars, CHART_CUSTOM_VAR_NAMES,
} from '../utils/sidebarPalette';
import {
  CHART_PALETTE_OPTIONS, CHART_PALETTE_IDS, DEFAULT_CHART_PALETTE, LODGE_CHART_PALETTE, CUSTOM_CHART_PALETTE, registerCustomChartPalette,
} from '../styles/chartColors';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Design system: Lodge Orange is the only brand accent. The previous
// Blue/Purple/Green accent options were removed so the app can't be themed
// off-brand. Orange values mirror src/styles/theme.css (#FF7A00 / 255 122 0).
export const ACCENT_COLORS = {
  ORANGE: 'orange',
};

const ACCENT_COLOR_CONFIGS = {
  [ACCENT_COLORS.ORANGE]: {
    name: 'Orange',
    primary: '#FF7A00',
    hover: '#E56E00',
    light: '#FFF4ED',
    dark: '#B35500',
    logoFilter: 'hue-rotate(8deg) saturate(1.6) brightness(1.2) contrast(1.1)',
    cssVars: {
      '--color-primary': '255 122 0',
      '--color-primary-hover': '229 110 0',
      '--color-accent': '255 122 0',
    }
  },
};

// Desktop sidebar palettes (Settings → Appearance). Token values live in
// src/styles/appShell.css under html[data-sidebar-theme]; `preview` only
// drives the swatches on the settings page. Keep ids in sync with the
// pre-paint script in public/index.html.
export const SIDEBAR_THEMES = [
  { id: 'onyx', tone: 'dark', name: 'Onyx', description: 'Near-black',
    preview: { bg: '#0B0B0D', line: '#3A3A44', active: '#1C1C22', text: '#D6D6E0' } },
  { id: 'graphite', tone: 'dark', name: 'Graphite', description: 'Softer charcoal',
    preview: { bg: '#1B1C21', line: '#44454F', active: '#2A2B32', text: '#D9DAE2' } },
  { id: 'midnight', tone: 'dark', name: 'Midnight', description: 'Deep navy',
    preview: { bg: '#0D1322', line: '#34405E', active: '#18213A', text: '#D3DAEA' } },
  { id: 'espresso', tone: 'dark', name: 'Espresso', description: 'Warm dark brown',
    preview: { bg: '#17110D', line: '#4A3A2F', active: '#261D17', text: '#E8DED6' } },
  { id: 'forest', tone: 'dark', name: 'Forest', description: 'Dark evergreen',
    preview: { bg: '#0C1512', line: '#2F4A3E', active: '#16241E', text: '#D4E2DA' } },
  { id: 'porcelain', tone: 'light', name: 'Porcelain', description: 'Cool soft white',
    preview: { bg: '#F4F4F7', line: '#CFCFD8', active: '#E6E6EC', text: '#3B3B45' } },
  { id: 'linen', tone: 'light', name: 'Linen', description: 'Warm off-white',
    preview: { bg: '#F7F3EC', line: '#D6CBBB', active: '#ECE5DA', text: '#3F3830' } },
  { id: 'apricot', tone: 'light', name: 'Apricot', description: 'Pale Lodgeic orange',
    preview: { bg: '#FFF4EA', line: '#EDCBAE', active: '#FFE6D1', text: '#4A3222' } },
  { id: 'mist', tone: 'light', name: 'Mist', description: 'Cool blue-grey',
    preview: { bg: '#EEF2F6', line: '#C6D0DD', active: '#E0E6EE', text: '#334052' } },
  { id: 'sage', tone: 'light', name: 'Sage', description: 'Pale green-grey',
    preview: { bg: '#EFF3EF', line: '#C4D0C7', active: '#E1E8E2', text: '#34423A' } },
  { id: 'match', tone: 'light', name: 'Match theme', description: 'Light in light mode, Onyx in dark',
    preview: { bg: '#F1F1F5', line: '#CFCFD8', active: '#E4E4EB', text: '#3B3B45' } },
];
// 'custom' = the viewer's own colour; its palette is derived at runtime.
export const CUSTOM_SIDEBAR_THEME = 'custom';
const DEFAULT_CUSTOM_SIDEBAR_COLOR = '#2B2D42';
const SIDEBAR_THEME_IDS = [...SIDEBAR_THEMES.map((t) => t.id), CUSTOM_SIDEBAR_THEME];
// Porcelain (light rail) is the default; Onyx is the CSS base in appShell.css
// and therefore the only id that needs no html attribute.
export const DEFAULT_SIDEBAR_THEME = 'porcelain';
const BASE_SIDEBAR_THEME = 'onyx';

const getInitialSidebarCustomColor = () => {
  try {
    return normalizeHex(localStorage.getItem('sidebarCustomColor')) || DEFAULT_CUSTOM_SIDEBAR_COLOR;
  } catch (e) {
    return DEFAULT_CUSTOM_SIDEBAR_COLOR;
  }
};

const getInitialSidebarTheme = () => {
  try {
    const stored = localStorage.getItem('sidebarTheme');
    return SIDEBAR_THEME_IDS.includes(stored) ? stored : DEFAULT_SIDEBAR_THEME;
  } catch (e) {
    return DEFAULT_SIDEBAR_THEME;
  }
};

const applySidebarTheme = (id, customColor) => {
  const root = document.documentElement;
  SIDEBAR_CUSTOM_VAR_NAMES.forEach((name) => root.style.removeProperty(name));
  let customVars = null;
  if (id === CUSTOM_SIDEBAR_THEME) {
    customVars = deriveSidebarPalette(customColor);
    if (customVars) {
      Object.entries(customVars).forEach(([name, value]) => {
        if (value) root.style.setProperty(name, value);
      });
    }
  }
  if (id && id !== BASE_SIDEBAR_THEME) root.setAttribute('data-sidebar-theme', id);
  else root.removeAttribute('data-sidebar-theme');
  // The pre-paint script (public/index.html) re-applies these verbatim.
  try {
    if (customVars) localStorage.setItem('sidebarCustomVars', JSON.stringify(customVars));
    else localStorage.removeItem('sidebarCustomVars');
  } catch (e) { /* private mode */ }
};

// Desktop shell: 'classic' (sidebar + floating tray, the pre-2026-09 look) or
// 'inset' (shadcn sidebar-inset with header). Persisted per user in Cosmos
// (uiPreferences.shell, synced by MainLayout) and mirrored to localStorage
// for the pre-paint script in public/index.html.
export const UI_SHELLS = [
  { id: 'classic', name: 'Classic', description: 'Light canvas, hover sidebar, floating tray' },
  { id: 'inset', name: 'New', description: 'Dark sidebar, content card, header with breadcrumb' },
];
const DEFAULT_UI_SHELL = 'classic';
const UI_SHELL_IDS = UI_SHELLS.map((s) => s.id);

const getInitialUiShell = () => {
  try {
    const stored = localStorage.getItem('uiShell');
    return UI_SHELL_IDS.includes(stored) ? stored : DEFAULT_UI_SHELL;
  } catch (e) {
    return DEFAULT_UI_SHELL;
  }
};

// The chosen shell applies at every width: phones get MobileInsetLayout
// (Layout/mobile) under 'inset' and the original MobileLayout under
// 'classic'. The viewport is still tracked for callers that need it.
const DESKTOP_QUERY = '(min-width: 769px)';
const isDesktopViewport = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(DESKTOP_QUERY).matches : true);
const applyUiShell = (id) => {
  document.documentElement.setAttribute('data-shell', id === 'inset' ? 'inset' : 'classic');
};

// Main panel (content card) colour, per mode: { light: id, dark: id } where
// id is a PANEL_THEMES id or 'custom'. Persisted as uiPreferences.panelTheme
// / panelCustomColor and mirrored to localStorage for the pre-paint script.
export const CUSTOM_PANEL_THEME = 'custom';
const readJson = (key, fallback) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || 'null');
    return parsed && typeof parsed === 'object' ? { ...fallback, ...parsed } : fallback;
  } catch (e) {
    return fallback;
  }
};
const normalizePanelTheme = (value) => {
  const src = value && typeof value === 'object' ? value : {};
  const pick = (mode) => {
    const id = src[mode];
    if (id === CUSTOM_PANEL_THEME) return id;
    const preset = PANEL_THEMES.find((t) => t.id === id);
    return preset && preset.tone === mode ? id : DEFAULT_PANEL_THEME[mode];
  };
  return { light: pick('light'), dark: pick('dark') };
};
const normalizePanelCustom = (value) => {
  const src = value && typeof value === 'object' ? value : {};
  return {
    light: normalizeHex(src.light) || DEFAULT_PANEL_CUSTOM_COLOR.light,
    dark: normalizeHex(src.dark) || DEFAULT_PANEL_CUSTOM_COLOR.dark,
  };
};
const getInitialPanelTheme = () => normalizePanelTheme(readJson('panelTheme', DEFAULT_PANEL_THEME));
const getInitialPanelCustomColor = () => normalizePanelCustom(readJson('panelCustomColor', DEFAULT_PANEL_CUSTOM_COLOR));
const applyPanelTheme = (theme, customColor, dark) => {
  const root = document.documentElement;
  const mode = dark ? 'dark' : 'light';
  const id = theme[mode];
  PANEL_CUSTOM_VAR_NAMES.forEach((name) => root.style.removeProperty(name));
  if (id === CUSTOM_PANEL_THEME) {
    root.setAttribute('data-panel-theme', CUSTOM_PANEL_THEME);
    const vars = derivePanelVars(customColor[mode]);
    if (vars) Object.entries(vars).forEach(([name, value]) => root.style.setProperty(name, value));
  } else if (id && id !== DEFAULT_PANEL_THEME[mode]) {
    root.setAttribute('data-panel-theme', id);
  } else {
    root.removeAttribute('data-panel-theme');
  }
  try {
    localStorage.setItem('panelTheme', JSON.stringify(theme));
    localStorage.setItem('panelCustomColor', JSON.stringify(customColor));
    const both = { light: derivePanelVars(customColor.light), dark: derivePanelVars(customColor.dark) };
    localStorage.setItem('panelCustomVars', JSON.stringify(both));
  } catch (e) { /* private mode */ }
};

// Custom data colour (one lead hex → whole chart palette). Only meaningful
// while chartPalette === 'custom'.
const DEFAULT_CHART_CUSTOM_COLOR = '#3B6EA8';
const getInitialChartCustomColor = () => {
  try {
    return normalizeHex(localStorage.getItem('chartCustomColor')) || DEFAULT_CHART_CUSTOM_COLOR;
  } catch (e) {
    return DEFAULT_CHART_CUSTOM_COLOR;
  }
};
const applyChartCustomVars = (paletteId, hex, dark) => {
  const root = document.documentElement;
  CHART_CUSTOM_VAR_NAMES.forEach((name) => root.style.removeProperty(name));
  if (paletteId !== CUSTOM_CHART_PALETTE) {
    registerCustomChartPalette(null);
    return;
  }
  registerCustomChartPalette(deriveChartPalette(hex));
  const vars = deriveChartVars(hex, dark);
  if (vars) Object.entries(vars).forEach(([name, value]) => root.style.setProperty(name, value));
  try {
    localStorage.setItem('chartCustomColor', hex);
    localStorage.setItem('chartCustomVars', JSON.stringify({ light: deriveChartVars(hex, false), dark: deriveChartVars(hex, true) }));
  } catch (e) { /* private mode */ }
};

// Chart palette (Settings → Appearance → Charts). Persisted per user in Cosmos
// (uiPreferences.chartPalette) and mirrored to localStorage for the pre-paint
// script. Only the New (inset) shell honours it — Classic always draws Lodge
// (the bare tokens) — so the html attribute is dropped whenever the inset
// shell isn't active, and set for every other id, including the default.
const CHART_PALETTE_CHOICES = [...CHART_PALETTE_IDS, CUSTOM_CHART_PALETTE];
const getInitialChartPalette = () => {
  try {
    const stored = localStorage.getItem('chartPalette');
    return CHART_PALETTE_CHOICES.includes(stored) ? stored : DEFAULT_CHART_PALETTE;
  } catch (e) {
    return DEFAULT_CHART_PALETTE;
  }
};
const applyChartPalette = (id, shell) => {
  const root = document.documentElement;
  if (id && id !== LODGE_CHART_PALETTE && shell === 'inset') root.setAttribute('data-chart-palette', id);
  else root.removeAttribute('data-chart-palette');
};

// Reads the same signals as the pre-paint inline script in public/index.html:
// explicit stored choice wins; first-time users default to light (we do not
// follow the OS prefers-color-scheme — light is the product default).
const getInitialDarkMode = () => {
  try {
    return localStorage.getItem('darkMode') === 'true';
  } catch (e) {
    return false;
  }
};

const getInitialAccent = () => {
  try {
    // Coerce any previously-saved (now-removed) accent to Orange.
    const stored = localStorage.getItem('accentColor');
    return ACCENT_COLOR_CONFIGS[stored] ? stored : ACCENT_COLORS.ORANGE;
  } catch (e) {
    return ACCENT_COLORS.ORANGE;
  }
};

export const ThemeProvider = ({ children }) => {
  // Synchronous initializers: the first React render already agrees with the
  // pre-paint script, so charts/components keyed off isDarkMode never flash
  // the light palette on reload.
  const [isDarkMode, setIsDarkMode] = useState(getInitialDarkMode);
  const [accentColor, setAccentColor] = useState(getInitialAccent);
  const [sidebarTheme, setSidebarThemeState] = useState(getInitialSidebarTheme);
  const [sidebarCustomColor, setSidebarCustomColorState] = useState(getInitialSidebarCustomColor);
  const [uiShell, setUiShellState] = useState(getInitialUiShell);
  const [chartPalette, setChartPaletteState] = useState(getInitialChartPalette);
  const [chartCustomColor, setChartCustomColorState] = useState(getInitialChartCustomColor);
  const [panelTheme, setPanelThemeState] = useState(getInitialPanelTheme);
  const [panelCustomColor, setPanelCustomColorState] = useState(getInitialPanelCustomColor);
  const [desktopViewport, setDesktopViewport] = useState(isDesktopViewport);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e) => setDesktopViewport(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange));
  }, []);
  const insetActive = uiShell === 'inset';
  useEffect(() => {
    applyUiShell(uiShell);
    applyChartPalette(chartPalette, uiShell);
    applyChartCustomVars(uiShell === 'inset' ? chartPalette : LODGE_CHART_PALETTE, chartCustomColor, isDarkMode);
  }, [uiShell, chartPalette, chartCustomColor, isDarkMode]);
  // The panel colour is per mode, so it re-applies on every light/dark flip.
  useEffect(() => {
    applyPanelTheme(panelTheme, panelCustomColor, isDarkMode);
  }, [panelTheme, panelCustomColor, isDarkMode]);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Reconcile the document on mount (covers stale classes after hot reload)
  useEffect(() => {
    applyThemeToDocument(isDarkMode, accentColor);
    applySidebarTheme(sidebarTheme, sidebarCustomColor);
    applyUiShell(uiShell);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (declared after the reconcile effect, which resets the colour on mount)
  // Phones on the New look: browser chrome (status bar, overscroll) takes the
  // sidebar palette, since the app bar is painted in it. Classic keeps the
  // canvas colour applyThemeToDocument sets.
  useEffect(() => {
    if (desktopViewport) return;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    const bg = insetActive
      ? getComputedStyle(document.documentElement).getPropertyValue('--sidebar-background').trim()
      : (isDarkMode ? '#121216' : '#F6F6FA');
    if (bg) meta.setAttribute('content', bg);
  }, [insetActive, desktopViewport, sidebarTheme, sidebarCustomColor, isDarkMode]);

  const applyThemeToDocument = (darkMode, accent) => {
    const root = document.documentElement;
    const config = ACCENT_COLOR_CONFIGS[accent] || ACCENT_COLOR_CONFIGS[ACCENT_COLORS.ORANGE];

    // Apply dark mode class
    if (darkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Keep mobile browser chrome on the app canvas color
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) themeMeta.setAttribute('content', darkMode ? '#121216' : '#F6F6FA');

    // Apply CSS custom properties for accent color
    Object.entries(config.cssVars).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    // Apply accent color class
    root.className = root.className.replace(/accent-\w+/g, '');
    root.classList.add(`accent-${accent}`);
  };

  const toggleDarkMode = () => {
    setIsTransitioning(true);
    const newDarkMode = !isDarkMode;
    
    setIsDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    
    // Add transition class
    document.documentElement.classList.add('theme-transition');
    
    applyThemeToDocument(newDarkMode, accentColor);
    
    // Remove transition class after animation
    setTimeout(() => {
      document.documentElement.classList.remove('theme-transition');
      setIsTransitioning(false);
    }, 300);
  };

  const changeAccentColor = (newAccent) => {
    setIsTransitioning(true);
    setAccentColor(newAccent);
    localStorage.setItem('accentColor', newAccent);
    
    // Add transition class
    document.documentElement.classList.add('theme-transition');
    
    applyThemeToDocument(isDarkMode, newAccent);
    
    // Remove transition class after animation
    setTimeout(() => {
      document.documentElement.classList.remove('theme-transition');
      setIsTransitioning(false);
    }, 300);
  };

  const setSidebarTheme = (id) => {
    const next = SIDEBAR_THEME_IDS.includes(id) ? id : DEFAULT_SIDEBAR_THEME;
    setSidebarThemeState(next);
    try {
      localStorage.setItem('sidebarTheme', next);
    } catch (e) { /* private mode — applies for this session only */ }
    document.documentElement.classList.add('theme-transition');
    applySidebarTheme(next, sidebarCustomColor);
    setTimeout(() => document.documentElement.classList.remove('theme-transition'), 300);
  };

  // Picking a colour also switches the sidebar to Custom. Called on every
  // colour-input change, so no transition class (it would lag the picker).
  const setSidebarCustomColor = (value) => {
    const hex = normalizeHex(value);
    if (!hex) return;
    setSidebarCustomColorState(hex);
    setSidebarThemeState(CUSTOM_SIDEBAR_THEME);
    try {
      localStorage.setItem('sidebarCustomColor', hex);
      localStorage.setItem('sidebarTheme', CUSTOM_SIDEBAR_THEME);
    } catch (e) { /* private mode */ }
    applySidebarTheme(CUSTOM_SIDEBAR_THEME, hex);
  };

  // Local apply only; MainLayout/AppearanceSettings own the Cosmos write.
  const setChartPalette = useCallback((id) => {
    const next = CHART_PALETTE_CHOICES.includes(id) ? id : DEFAULT_CHART_PALETTE;
    setChartPaletteState(next);
    try {
      localStorage.setItem('chartPalette', next);
    } catch (e) { /* private mode */ }
  }, []);

  // Picking a data colour also switches the palette to Custom (vars are
  // applied by the effect above).
  const setChartCustomColor = useCallback((value) => {
    const hex = normalizeHex(value);
    if (!hex) return;
    setChartCustomColorState(hex);
    setChartPaletteState(CUSTOM_CHART_PALETTE);
    try {
      localStorage.setItem('chartPalette', CUSTOM_CHART_PALETTE);
    } catch (e) { /* private mode */ }
  }, []);

  // Panel colour for the CURRENT mode (light or dark). `setPanelThemes` takes
  // the whole { light, dark } object (server sync).
  const setPanelTheme = useCallback((id) => {
    setPanelThemeState((prev) => normalizePanelTheme({ ...prev, [isDarkMode ? 'dark' : 'light']: id }));
    document.documentElement.classList.add('theme-transition');
    setTimeout(() => document.documentElement.classList.remove('theme-transition'), 300);
  }, [isDarkMode]);
  const setPanelThemes = useCallback((value) => setPanelThemeState(normalizePanelTheme(value)), []);
  const setPanelCustomColor = useCallback((value) => {
    const hex = normalizeHex(value);
    if (!hex) return;
    const mode = isDarkMode ? 'dark' : 'light';
    setPanelCustomColorState((prev) => ({ ...prev, [mode]: hex }));
    setPanelThemeState((prev) => ({ ...prev, [mode]: CUSTOM_PANEL_THEME }));
  }, [isDarkMode]);
  const setPanelCustomColors = useCallback((value) => setPanelCustomColorState(normalizePanelCustom(value)), []);

  // Local apply only; MainLayout/AppearanceSettings own the Cosmos write.
  const setUiShell = useCallback((id) => {
    const next = UI_SHELL_IDS.includes(id) ? id : DEFAULT_UI_SHELL;
    setUiShellState(next);
    try {
      localStorage.setItem('uiShell', next);
    } catch (e) { /* private mode */ }
    applyUiShell(next);
  }, []);

  // Back to the New look's true defaults: sidebar, main panel and data
  // colours, plus their pre-paint mirrors in localStorage. Dark mode and the
  // accent colour are shared with Classic and stay. Called when the viewer
  // switches to Classic, so a later return to New starts clean (the Cosmos
  // copy is cleared by AppearanceSettings alongside the shell write).
  const resetNewLookPreferences = useCallback(() => {
    setSidebarThemeState(DEFAULT_SIDEBAR_THEME);
    setSidebarCustomColorState(DEFAULT_CUSTOM_SIDEBAR_COLOR);
    setChartPaletteState(DEFAULT_CHART_PALETTE);
    setChartCustomColorState(DEFAULT_CHART_CUSTOM_COLOR);
    setPanelThemeState(normalizePanelTheme(DEFAULT_PANEL_THEME));
    setPanelCustomColorState(normalizePanelCustom(DEFAULT_PANEL_CUSTOM_COLOR));
    try {
      [
        'sidebarTheme', 'sidebarCustomColor', 'sidebarCustomVars',
        'chartPalette', 'chartCustomColor', 'chartCustomVars',
        'panelTheme', 'panelCustomColor', 'panelCustomVars',
      ].forEach((key) => localStorage.removeItem(key));
    } catch (e) { /* private mode */ }
    applySidebarTheme(DEFAULT_SIDEBAR_THEME, DEFAULT_CUSTOM_SIDEBAR_COLOR);
  }, []);

  const getCurrentAccentConfig = () => {
    return ACCENT_COLOR_CONFIGS[accentColor];
  };

  const value = {
    isDarkMode,
    accentColor,
    isTransitioning,
    toggleDarkMode,
    changeAccentColor,
    getCurrentAccentConfig,
    accentColors: ACCENT_COLORS,
    accentColorConfigs: ACCENT_COLOR_CONFIGS,
    sidebarTheme,
    setSidebarTheme,
    sidebarThemes: SIDEBAR_THEMES,
    sidebarCustomColor,
    setSidebarCustomColor,
    uiShell,
    setUiShell,
    resetNewLookPreferences,
    uiShells: UI_SHELLS,
    chartPalette,
    setChartPalette,
    chartPalettes: CHART_PALETTE_OPTIONS,
    chartCustomColor,
    setChartCustomColor,
    chartCustomPalette: chartPalette === CUSTOM_CHART_PALETTE ? deriveChartPalette(chartCustomColor) : null,
    // main panel: the current mode's choice plus the per-mode objects for sync
    panelTheme: panelTheme[isDarkMode ? 'dark' : 'light'],
    panelThemes: PANEL_THEMES,
    panelThemeByMode: panelTheme,
    setPanelTheme,
    setPanelThemes,
    panelCustomColor: panelCustomColor[isDarkMode ? 'dark' : 'light'],
    panelCustomColorByMode: panelCustomColor,
    setPanelCustomColor,
    setPanelCustomColors,
    // true when the New (inset) shell is chosen — desktop and phone alike
    insetActive,
    isDesktopViewport: desktopViewport,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 